import { Injectable, OnDestroy, signal, Signal } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';

export interface HotkeyBinding {
  /** Unique identifier for this binding — used to unregister it later. */
  id: string;
  /**
   * The key combo to listen for.
   * Modifiers: 'mod' (⌘ on Mac, Ctrl on Windows/Linux), 'meta' (Cmd/Win), 'ctrl', 'shift', 'alt'.
   * Keys are separated by '+', e.g. 'mod+k', 'mod+shift+e', 'escape'.
   * Use lowercase for the key character.
   *
   * Prefer 'mod' over 'meta' or 'ctrl' for cross-platform shortcuts — it resolves
   * to the platform-native primary modifier automatically.
   *
   * For double-tap bindings (e.g. double Shift), set `doubleTap: true` and
   * use a single modifier name as the key, e.g. keys: 'shift'.
   */
  keys: string;
  /**
   * Called when the combo fires and the binding is active.
   * Omit to register a display-only entry that appears in the hotkey index
   * but whose execution is handled elsewhere (e.g. via @HostListener).
   */
  callback?: (event: KeyboardEvent) => void;
  /**
   * When true the browser's default action for this key combo is suppressed.
   * Defaults to true.
   */
  preventDefault?: boolean;
  /**
   * When true, the binding fires when the same key is pressed twice within
   * 300 ms with no other key pressed in between.
   */
  doubleTap?: boolean;
  /**
   * Human-readable label shown in the hotkey index, e.g. 'Open action menu'.
   * Translation keys are supported — pass the key and translate in the template.
   * If omitted the binding will not appear in the hotkey index.
   */
  label?: string;
  /**
   * Group name for the hotkey index, e.g. 'hotkeys.group.navigation'.
   * Bindings with the same group are shown together.
   * Defaults to 'hotkeys.group.page' if omitted.
   */
  group?: string;
}

/**
 * Centralised hotkey registry.
 *
 * Register bindings from any component or service:
 *
 * ```typescript
 * this.hotkeyService.register({
 *   id: 'create-mp',
 *   keys: 'mod+shift+m',
 *   label: 'hotkeys.label.createMeteringPoint',
 *   group: 'hotkeys.group.navigation',
 *   callback: () => this.router.navigate(['/meteringpoint/create']),
 * });
 * ```
 *
 * Always unregister when the component is destroyed:
 *
 * ```typescript
 * ngOnDestroy() {
 *   this.hotkeyService.unregister('create-mp');
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class HotkeyService implements OnDestroy {
  private readonly bindingsMap = new Map<string, HotkeyBinding>();
  private readonly bindingsSignal = signal<HotkeyBinding[]>([]);

  /** True when running on macOS — used to resolve the 'mod' modifier. */
  private readonly isMac = /mac/i.test(navigator.userAgent);

  /**
   * Read-only signal of all currently registered bindings that have a label.
   * Used by the hotkey index overlay to display available shortcuts.
   */
  public readonly bindings: Signal<HotkeyBinding[]> = this.bindingsSignal.asReadonly();

  private readonly subscription: Subscription;

  /** Tracks double-tap state: the key of the last tap and when it happened. */
  private lastTapKey: string | null = null;
  private lastTapTime: number = 0;
  private readonly doubleTapWindow = 300;

  constructor() {
    this.subscription = fromEvent<KeyboardEvent>(document, 'keydown').subscribe(event => {
      this.handleKeydown(event);
    });
  }

  /**
   * Registers a hotkey binding. If a binding with the same id already exists it
   * is silently replaced.
   */
  public register(binding: HotkeyBinding): void {
    this.bindingsMap.set(binding.id, { preventDefault: true, ...binding });
    this.syncSignal();
  }

  /**
   * Removes a previously registered binding by id.
   */
  public unregister(id: string): void {
    this.bindingsMap.delete(id);
    this.syncSignal();
  }

  /**
   * Removes all registered bindings at once.
   */
  public unregisterAll(): void {
    this.bindingsMap.clear();
    this.syncSignal();
  }

  /**
   * Returns a human-readable label for a key combo string, e.g. 'meta+m' → '⌘ M'.
   * Pass doubleTap: true to repeat the label, e.g. 'shift' → '⇧ ⇧'.
   */
  public formatLabel(keys: string, doubleTap?: boolean): string {
    const modifierLabels: Record<string, string> = {
      mod:        this.isMac ? '⌘' : '⌃',
      meta:       this.isMac ? '⌘' : '⊞',
      ctrl:       '⌃',
      shift:      '⇧',
      alt:        this.isMac ? '⌥' : 'Alt',
      arrowup:    '↑',
      arrowdown:  '↓',
      arrowleft:  '←',
      arrowright: '→',
    };

    const label = keys
      .split('+')
      .map(part => modifierLabels[part.toLowerCase()] ?? part.toUpperCase())
      .join(' ');

    return doubleTap ? `${label} ${label}` : label;
  }

  private syncSignal(): void {
    this.bindingsSignal.set(Array.from(this.bindingsMap.values()));
  }

  private handleKeydown(event: KeyboardEvent): void {
    // Skip when the user is typing inside an input, textarea, or contenteditable
    const target = event.target as HTMLElement;
    const tag = target?.tagName?.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) {
      this.resetDoubleTap();
      return;
    }

    const now = Date.now();

    for (const binding of this.bindingsMap.values()) {
      if (binding.doubleTap) {
        if (this.matchesDoubleTap(event, binding.keys, now)) {
          if (!binding.callback) continue; // display-only entry — skip execution
          if (binding.preventDefault !== false) {
            event.preventDefault();
          }
          this.resetDoubleTap();
          binding.callback(event);
          return;
        }
      } else if (this.matches(event, binding.keys)) {
        if (!binding.callback) continue; // display-only entry — skip execution
        if (binding.preventDefault !== false) {
          event.preventDefault();
        }
        binding.callback(event);
        return;
      }
    }

    // Any key that is not the watched double-tap key cancels the sequence
    this.updateDoubleTapState(event, now);
  }

  private matchesDoubleTap(event: KeyboardEvent, keys: string, now: number): boolean {
    const key = keys.toLowerCase();
    const eventKey = event.key.toLowerCase();

    if (eventKey !== key) {
      this.resetDoubleTap();
      return false;
    }

    const elapsed = now - this.lastTapTime;

    if (this.lastTapKey === key && elapsed <= this.doubleTapWindow) {
      return true;
    }

    this.lastTapKey = key;
    this.lastTapTime = now;
    return false;
  }

  private updateDoubleTapState(event: KeyboardEvent, now: number): void {
    const eventKey = event.key.toLowerCase();
    if (this.lastTapKey !== null && eventKey !== this.lastTapKey) {
      this.resetDoubleTap();
    }
  }

  private resetDoubleTap(): void {
    this.lastTapKey = null;
    this.lastTapTime = 0;
  }

  private matches(event: KeyboardEvent, keys: string): boolean {
    const parts = keys.toLowerCase().split('+');
    const key = parts.at(-1)!;

    // 'mod' expands to 'meta' on Mac and 'ctrl' on Windows/Linux
    const hasMod = parts.includes('mod');
    const requiresMeta  = parts.includes('meta') || (hasMod &&  this.isMac);
    const requiresCtrl  = parts.includes('ctrl') || (hasMod && !this.isMac);
    const requiresShift = parts.includes('shift');
    const requiresAlt   = parts.includes('alt');

    // On macOS, Option (Alt) + letter produces a composed character (e.g. Alt+R → '®')
    // so event.key no longer matches the bare letter. Use event.code as a layout-independent
    // fallback — 'KeyR' → 'r', 'Digit1' → '1', 'Space' → 'space', etc.
    const physicalKey = this.codeToKey(event.code);

    return (
      (event.key.toLowerCase() === key || physicalKey === key) &&
      event.metaKey  === requiresMeta  &&
      event.ctrlKey  === requiresCtrl  &&
      event.shiftKey === requiresShift &&
      event.altKey   === requiresAlt
    );
  }

  /**
   * Derives a normalised single-character key name from a `KeyboardEvent.code` value.
   * This is layout-independent: 'KeyR' → 'r', 'Digit3' → '3', 'Space' → 'space'.
   */
  private codeToKey(code: string): string {
    if (code.startsWith('Key'))   return code.slice(3).toLowerCase();
    if (code.startsWith('Digit')) return code.slice(5);
    return code.toLowerCase();
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
